import React, { FC } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'Shared/Hooks/useTranslation';
import Swiper from 'react-id-swiper/lib/custom';

import useIsClient from 'Jsx/Shared/Hooks/useIsClient';
import { getString } from 'Utils/randomizer';
import GET_COURSE_DETAIL_PROJECTS from '../graphql/GetCourseDetailProjects.gcg.graphql';
import {
  GetCourseDetailProjectsQuery,
  GetCourseDetailProjectsQueryVariables,
} from '../graphql/__generated__/GetCourseDetailProjects.gcg';

import ProjectSkeleton from '../components/ProjectSkeleton';
import ProjectCard from '../components/ProjectCard';
import { ProjectType } from '../types';
import {
  ProjectsContainer,
  ProjectsContainerDesktop,
  WrapperSlider,
  RestToProjectsDesktop,
} from '../styles';
import locales from '../../../i18n/localeEs';
import matchMediaQuery from 'Jsx/Utils/matchMediaQuery';

const translations = locales.es;

const params = {
  slidesPerView: 2.2,
  spaceBetween: 16,
  rebuildOnUpdate: true,
  breakpoints: {
    320: {
      spaceBetween: 16,
      slidesPerView: 1.2,
    },
    450: {
      spaceBetween: 16,
      slidesPerView: 1.5,
    },
    596: {
      slidesPerView: 1.8,
      spaceBetween: 16,
    },
  },
};

type ProjectListProps = {
  courseId: string;
  courseIsFree?: boolean;
};

const ProjectList: FC<ProjectListProps> = ({ courseId, courseIsFree }) => {
  const { t } = useTranslation();
  const [projectsShow, setProjectsShow] = React.useState<ProjectType[]>([]);
  const [restProjectsShow, setRestProjectsShow] = React.useState<ProjectType[]>(
    [],
  );
  const isClient = useIsClient();
  const { data, loading, error } = useQuery<
    GetCourseDetailProjectsQuery,
    GetCourseDetailProjectsQueryVariables
  >(GET_COURSE_DETAIL_PROJECTS, {
    variables: {
      courseId,
      certified: !courseIsFree ? true : undefined,
    },
  });
  let INITIAL_PROJECTS_SHOW = 5;

  if (isClient) {
    INITIAL_PROJECTS_SHOW = matchMediaQuery('lg').matches ? 5 : 4;
  }
  const projects = React.useMemo(() => {
    return data && data.projects
      ? data.projects?.edges.map(project => project.node)
      : [];
  }, [data]);

  React.useEffect(() => {
    const firstProjects = projects.slice(0, INITIAL_PROJECTS_SHOW);

    setProjectsShow(firstProjects);
  }, [data]);

  const showMoreProjects = () => {
    const allProjectsNoRepeats = projects.slice(
      INITIAL_PROJECTS_SHOW,
      projects.length,
    );

    setRestProjectsShow([...allProjectsNoRepeats]);
  };

  const renderProjects = (isInSlider = false) => {
    const isShowMore = i => {
      return (
        restProjectsShow.length === 0 &&
        projectsShow.length - 1 === i &&
        projects.length >= INITIAL_PROJECTS_SHOW
      );
    };

    return projectsShow.map((project, i) =>
      isInSlider ? (
        <div key={project.originalId}>
          <ProjectCard project={project} />
        </div>
      ) : (
        <ProjectCard
          project={project}
          key={project.originalId}
          showMoreProjects={showMoreProjects}
          isShowMore={isShowMore(i)}
          numRestItems={projects.length - projectsShow.length}
        />
      ),
    );
  };

  const renderProjectsDesktop = () => {
    const [project1, ...restProjects] = projectsShow;

    const isShowMore = i => {
      return (
        restProjectsShow.length === 0 &&
        restProjects.length - 1 === i &&
        projects.length >= INITIAL_PROJECTS_SHOW
      );
    };

    return (
      <>
        <ProjectCard project={project1} />
        <div className="isDesktop">
          {restProjects.map((project, i) => (
            <ProjectCard
              project={project}
              key={project.originalId}
              showMoreProjects={showMoreProjects}
              isShowMore={isShowMore(i)}
              numRestItems={projects.length - projectsShow.length}
            />
          ))}
        </div>
      </>
    );
  };

  const renderRestToProjectsDesktop = () => {
    if (restProjectsShow.length === 0) {
      return null;
    }
    return (
      <RestToProjectsDesktop>
        {restProjectsShow.map((project, i) => (
          <ProjectCard
            project={project}
            key={project.originalId}
            showMoreProjects={showMoreProjects}
            numRestItems={projects.length - 5}
          />
        ))}
      </RestToProjectsDesktop>
    );
  };

  if (error || projectsShow.length <= 0) {
    return null;
  }

  return (
    <div id="Course_Detail__projects">
      <section className="mb-48">
        <h2 className="mb-24 font-h4">
          {t('PROJECTS_TITLE', {
            defaultValue: translations.PROJECTS_TITLE,
          })}
        </h2>
        <WrapperSlider className="flex w-full md:hidden">
          <Swiper {...params}>
            {loading
              ? [...Array(6).keys()].map(() => (
                  <div key={getString()}>
                    <ProjectSkeleton />
                  </div>
                ))
              : renderProjects(true)}
          </Swiper>
        </WrapperSlider>
        <div className="hidden md:block lg:hidden">
          <ProjectsContainer>
            {loading
              ? [...Array(4).keys()].map(() => (
                  <ProjectSkeleton key={getString()} />
                ))
              : renderProjects()}
          </ProjectsContainer>
          {renderRestToProjectsDesktop()}
        </div>
        <div className="hidden lg:block">
          <ProjectsContainerDesktop>
            {loading
              ? [...Array(2).keys()].map(() => (
                  <ProjectSkeleton key={getString()} />
                ))
              : renderProjectsDesktop()}
          </ProjectsContainerDesktop>
          {renderRestToProjectsDesktop()}
        </div>
      </section>
    </div>
  );
};

export default ProjectList;
